// El shell DESKTOP de los modos con el diseño nuevo, para los modos que viven en su
// propia ruta y no adentro de `PresetDetailSheet` —
// ADR-2026-09-07-animar-imagen-toma-el-diseno-de-los-modos.
//
// Es la geometría de Automática / Modelo / Catálogo, que hasta ahora existía UNA sola
// vez: adentro de `PresetDetailSheet`, mezclada con los pasos, los pickers y el estado
// de esos modos. Un modo con ruta propia (Animar imagen) no puede montar ese sheet —no
// tiene productos, ni pasos, ni preset—, así que lo que se comparte es esto: el marco,
// la fila y las dos cajas. Lo de adentro lo pone cada modo.
//
// Qué dibuja, de afuera hacia adentro:
//
//   • El PANEL `fixed` a la derecha de la sidebar. Va sin superficie propia —se apoya
//     en el lienzo del shell, como `/plantillas`— y arranca debajo de la barra de
//     créditos (ADR-2026-08-03-paneles-de-modo-arrancan-bajo-la-barra). Los 52px de
//     arriba son el aire de una sección del shell, que a un hijo `fixed` no lo alcanza.
//   • La FILA de contenido, con `FILA_MODO_REDISENO` (52px por lado, el mismo aire que
//     arriba): el panel de configuración a la izquierda y la muestra a la derecha.
//   • El PANEL DE CONFIGURACIÓN: card blanca de ancho fijo (`ANCHO_PANEL_CONFIG`) con la
//     cabecera adentro —breadcrumb + `<h1>` de 21px— y el contenido del modo debajo.
//   • La MUESTRA: la columna que se lleva el ancho que sobra.
//
// Sólo desktop (`hidden md:flex`): en mobile cada modo tiene su propio layout, que no
// es de dos columnas.

import React from 'react';
import { ChevronLeft } from 'lucide-react';
import {
  EncabezadoPantalla,
  MARCO_PANTALLA,
} from '../../../../components/PantallaChrome';
import type { Miga } from '../../../../components/Migas';
import {
  ANCHO_PANEL_CONFIG,
  COLUMNA_RESULTADOS_AL_BORDE,
  FILA_MODO_REDISENO,
} from '../../components/layoutModo';

export function ShellModoRedisenado({
  sidebarWidth,
  migas,
  titulo,
  onVolver,
  panel,
  muestra,
  muestraAlBorde = false,
  cederZIndex = false,
  panelLateral,
}: {
  /** Ancho de la sidebar del shell: de dónde arranca el panel. */
  sidebarWidth: number;
  /** Breadcrumb de la cabecera. Los modos arman `[Inicio, <modo>]`. */
  migas?: Miga[];
  /** El `<h1>` de la cabecera. Cambiarlo remonta el título y le hace su fade. */
  titulo: string;
  /** Respaldo del breadcrumb: el "Volver" que se dibuja si no hay migas. */
  onVolver?: () => void;
  /** Lo que va DENTRO de la card blanca, debajo de la cabecera. */
  panel: React.ReactNode;
  /** La columna de la derecha: la muestra del modo, o lo que la reemplace. */
  muestra: React.ReactNode;
  /**
   * La `muestra` es la COLUMNA DE RESULTADOS: se sale de los paddings de la fila y llega
   * al marco arriba, abajo y a la derecha —
   * ADR-2026-09-06-la-columna-de-resultados-llega-al-marco, el mismo tratamiento que le
   * da `PresetDetailSheet` a las tandas.
   *
   * Los márgenes negativos cancelan paddings CONCRETOS de este shell (el `px-8` y el
   * `pb-6` de la fila, el `lg:!pr-[52px]` de `FILA_MODO_REDISENO` y el `lg:pt-[52px]` del
   * panel), así que sólo valen cuando la columna de verdad los tiene que cancelar: con la
   * muestra del modo adentro se queda en su caja.
   */
  muestraAlBorde?: boolean;
  /**
   * Baja el z del panel para que el visor de creaciones —lightbox `fixed z-50`
   * portalizado al body— quede por encima. Sin esto el visor se abre DEBAJO y sólo se ve
   * su velo negro; subirle el z al lightbox no sirve, porque sus propios menús son
   * `z-[200]`. Es el `cederZIndex` de `PresetDetailSheet`.
   */
  cederZIndex?: boolean;
  /**
   * El `PanelLateralModo` del modo — la capa donde se elige lo que la columna no
   * resuelve en su lugar. Se dibuja SOBRE el área de la derecha, dejando el panel de
   * configuración a la vista y usable.
   *
   * Es una función y no un nodo porque las dos cosas que el panel lateral necesita las
   * sabe este shell y no el modo: el contenedor contra el que se mide el click-afuera
   * (la fila) y dónde arranca (el borde derecho del panel de configuración, medido).
   */
  panelLateral?: (args: {
    contenedorRef: React.RefObject<HTMLDivElement | null>;
    offsetIzquierdo: number | string;
  }) => React.ReactNode;
}) {
  /**
   * Dónde arranca el panel lateral: el borde derecho del panel de configuración, medido
   * y no escrito — ADR-2026-09-02-el-panel-lateral-arranca-donde-termina-la-configuracion.
   *
   * El ancho de la columna es elástico (`ANCHO_PANEL_CONFIG` es un `clamp`), así que un
   * número acá la taparía en cuanto cambiara la ventana. Se observan los DOS elementos:
   * el contenedor cubre el resize y el panel, cualquier cambio propio.
   */
  const filaRef = React.useRef<HTMLDivElement | null>(null);
  const panelConfigRef = React.useRef<HTMLElement | null>(null);
  const [offsetPanelLateral, setOffsetPanelLateral] = React.useState<number | null>(
    null,
  );
  React.useLayoutEffect(() => {
    const fila = filaRef.current;
    const config = panelConfigRef.current;
    if (!fila || !config || !panelLateral) return;
    const medir = () => {
      const f = fila.getBoundingClientRect();
      const c = config.getBoundingClientRect();
      // + el `gap-6` de la fila: el flotante arranca donde arrancaría la columna de al
      // lado, no pegado al borde del panel.
      setOffsetPanelLateral(Math.round(c.right - f.left + 24));
    };
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(fila);
    ro.observe(config);
    return () => ro.disconnect();
  }, [panelLateral]);

  return (
    <div
      // Las mismas clases que el panel de los modos-sección en `PresetDetailSheet`. Sin
      // `bg-*` (el color va por `style`: es la variable del lienzo del shell) y sin
      // `BORDE_PANTALLA`: estos modos no llevan borde porque no son una tarjeta que
      // flota sobre el lienzo — SON el lienzo (ADR-2026-08-03-anuncio-se-ve-como-seccion).
      className={`hidden md:flex fixed inset-y-0 right-0 ${
        cederZIndex ? 'z-30' : 'z-[100]'
      } rounded-tl-[26px] rounded-bl-[26px] lg:pt-[52px] lg:!right-0 ${MARCO_PANTALLA} flex-col overflow-hidden overscroll-contain`}
      style={{
        left: sidebarWidth,
        // Debajo de la barra de créditos del shell en vez de taparla. La variable la
        // publica `EstiloMinimal` midiendo la barra; vale 0 cuando no está.
        top: 'var(--shell-barra-creditos, 0px)',
        // El panel no puede ser transparente —se monta ENCIMA del contenido del shell—
        // pero tampoco lleva superficie de tarjeta: el color es el del lienzo, así que
        // se sigue leyendo como sección. El fallback es el lienzo del shell viejo.
        background: 'var(--plantillas-sticky-bg, #FBFBFD)',
      }}
    >
      {/* Panel a la izquierda, muestra a la derecha, en ese orden también en el DOM: es
          el orden en que se lee y en que se opera la pantalla. (El sheet de los otros
          modos llega al mismo dibujo por `flex-row-reverse`, con la muestra primero en
          el DOM; acá no hay motivo para esa vuelta.) `items-stretch` es lo que hace que
          las dos columnas midan lo mismo de alto. */}
      <div
        ref={filaRef}
        className={`relative px-8 pb-6 flex gap-6 ${FILA_MODO_REDISENO} flex-1 min-h-0 items-stretch`}
      >
        <aside
          ref={panelConfigRef}
          // Ancho FIJO y elástico, el mismo de todos los modos del rediseño: la muestra
          // se lleva lo que sobra. `grow-0 shrink-0` para que no la ceda cuando el
          // contenido de la otra columna empuja.
          style={{ flexBasis: ANCHO_PANEL_CONFIG, width: ANCHO_PANEL_CONFIG }}
          className="grow-0 shrink-0 flex flex-col bg-white rounded-2xl shadow-sm ring-1 ring-black/[0.04] overflow-hidden"
        >
          <div className="animate-mode-swap flex-1 min-h-0 flex flex-col">
            {/* La cabecera vive DENTRO del panel, en su borde de arriba
                (ADR-2026-09-06-el-encabezado-del-panel-no-cambia-de-paso). Afuera se
                come una franja del alto por encima de las dos columnas; adentro, la
                fila arranca pegada al techo. */}
            <div className="shrink-0">
              <div className="px-6 pt-5">
                <EncabezadoPantalla
                  migas={migas}
                  className="!px-0 !pt-0 !pb-2"
                  respaldo={
                    onVolver ? (
                      <button
                        type="button"
                        onClick={onVolver}
                        className="inline-flex items-center gap-1 text-brand-primary text-[14px] hover:opacity-75 transition-opacity"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        Volver
                      </button>
                    ) : undefined
                  }
                />
                {/* El título, EXACTAMENTE como el de los otros modos: 32px, 700, altura
                    de línea 1.2 y -0.02em.
                    Esos valores se escriben acá y no se heredan porque en los otros modos
                    no salen de sus clases —dicen `text-[21px] font-semibold` y no se ven
                    nunca— sino de la regla global `.shell-seccion h1`, que va con
                    `!important` y les gana. Este panel vive en su propia ruta, fuera de
                    ese wrapper, así que ahí quedaba en 21px: más chico que el de al lado
                    sin que ninguna clase lo dijera. El color no se fija: la regla global
                    lo deja en `inherit`, o sea el negro del shell.

                    El peso va como `font-[700]` y no como `font-bold`: en este proyecto
                    `bold` está redefinido en 600, así que la utilidad no llega al 700
                    que pone la regla global y el título quedaba medio tono más liviano
                    que el de los otros modos.

                    `key` por título: al cambiar de paso el `<h1>` se remonta y hace su
                    fade. Sin desplazamiento — el movimiento lo lleva la columna de al
                    lado, y esta caja es justamente la que NO se mueve. */}
                <h1
                  key={titulo}
                  className="animate-paso-titulo text-[32px] font-[700] leading-[1.2] tracking-[-0.02em]"
                >
                  {titulo}
                </h1>
              </div>
            </div>
            {panel}
          </div>
        </aside>

        <div
          className={`min-w-0 flex flex-col flex-1 ${
            muestraAlBorde ? COLUMNA_RESULTADOS_AL_BORDE : ''
          }`}
        >
          {muestra}
        </div>

        {/* El panel lateral va DENTRO de la fila: se posiciona `absolute` contra ella,
            que es lo que lo deja tapando sólo el área de la derecha. El `calc` es el
            respaldo del primer render, antes de que exista la medición. */}
        {panelLateral?.({
          contenedorRef: filaRef,
          offsetIzquierdo:
            offsetPanelLateral ?? `calc(${ANCHO_PANEL_CONFIG} + 1.5rem)`,
        })}
      </div>
    </div>
  );
}

export default ShellModoRedisenado;
